import { defineStore } from 'pinia';
interface ItemsDimensions {
  height?: number;
  width?: number;
}
interface ProductImagesCtaState {
  itemsList: object[];
  itemsOrder: string[];
  itemsDimensions: Record<string, ItemsDimensions>;
}
interface ItemData {
  customCssClass: string | null;
  customStyle: string | null;
  id: string | null;
  isAR: boolean | null;
  label: string | null;
  onClickHandler: (() => void) | null;
  showArrow: boolean | null;
  url?: string | null;
  allowOpeningInNewTab: boolean;
}

export default defineStore('productImagesCtaStore', {
  state: (): ProductImagesCtaState => ({
    // The list of CTA items to be displayed
    itemsList: [],
    // The reference array to sort itemsList
    itemsOrder: [],
    // CTA Button items Dimensions
    itemsDimensions: {},
  }),

  getters: {
    // Items sorted based on itemsOrder; items not in itemsOrder go last.
    sortedList: (state: ProductImagesCtaState): ItemData[] => {
      const originalArray = state.itemsList.slice();
      const sortCallback = (a, b) => {
        const inA = state.itemsOrder.includes(a.id);
        const inB = state.itemsOrder.includes(b.id);
        if (!inA && inB) return 1;
        if (inA && !inB) return -1;
        return state.itemsOrder.indexOf(a.id) - state.itemsOrder.indexOf(b.id);
      };
      return originalArray.sort(sortCallback);
    },

    /**
     * All CTA dimensions
     * @param {ProductImagesCtaState} state
     * @returns {Record<string, ItemsDimensions>}
     */
    allDimensions: (state) => state.itemsDimensions,
  },

  actions: {
    // Add (or upsert) an item.
    addItem(itemData: ItemData) {
      const idx = this.itemsList.findIndex((x) => x.id === itemData?.id);
      if (idx >= 0) {
        // Merge to preserve reactivity
        this.itemsList.splice(idx, 1, { ...this.itemsList[idx], ...itemData });
      } else {
        this.itemsList.push(itemData);
      }
    },

    // Remove an item by id (noop if not found)
    removeItemById(itemId: string) {
      const index = this.itemsList.findIndex((x) => x.id === itemId);
      if (index > -1) this.itemsList.splice(index, 1);
    },

    // Update an item by id (merges fields)
    updateItemById({ itemId, itemData }: { itemId: string; itemData: ItemData }) {
      const index = this.itemsList.findIndex((x) => x.id === itemId);
      if (index > -1 && itemData) {
        const next = { ...this.itemsList[index], ...itemData };
        this.itemsList.splice(index, 1, next);
      }
    },

    // Replace order reference array
    setItemsOrder(ids: string[]) {
      this.itemsOrder = Array.isArray(ids) ? ids.slice() : [];
    },

    // Empty the items list
    resetItems() {
      this.itemsList = [];
    },

    // Ensure a dimensions entry exists for a given key
    ensureDimensionsEntry(key: string) {
      if (!this.itemsDimensions?.[key]) {
        this.itemsDimensions[key] = { height: 0, width: 0 };
      }
    },

    // Set height for a given dimensions key
    setItemsHeight({ key, height }: { key: string; height: number }) {
      const dim = this.itemsDimensions[key];
      if (dim) dim.height = height;
    },

    // Set width for a given dimensions key
    setItemsWidth({ key, width }: { key: string; width: number }) {
      const dim = this.itemsDimensions[key];
      if (dim) dim.width = width;
    },
  },
});
